import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, Outlet } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import { SiteProvider, MeProvider, useMe } from "@/lib/site";
import { Spinner } from "@/components/site/Bits";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import Home from "@/pages/Home";
import Courses from "@/pages/Courses";
import CourseDetail from "@/pages/CourseDetail";
import Enroll from "@/pages/Enroll";
import PaymentResult from "@/pages/PaymentResult";
import Account from "@/pages/Account";
import { About, Contact, InfoPage, FAQ, Verify } from "@/pages/Pages";
import { SignInPage, SignUpPage, ForgotPasswordPage, ResetPasswordPage, VerifyEmailPage } from "@/pages/Auth";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminCourses from "@/pages/admin/Courses";
import AdminStudents from "@/pages/admin/Students";
import AdminPayments from "@/pages/admin/Payments";
import AdminCoupons from "@/pages/admin/Coupons";
import AdminMeetings from "@/pages/admin/Meetings";
import AdminReports from "@/pages/admin/Reports";
import AdminContent from "@/pages/admin/Content";
import AdminSystem from "@/pages/admin/System";

const ScrollTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const RequireAuth = ({ children }) => {
  const loc = useLocation();
  const { me, loading } = useMe();
  if (loading) return <Spinner />;
  if (!me) return <Navigate to={`/sign-in?redirect=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  return children;
};

const SiteLayout = () => (
  <div className="min-h-screen flex flex-col">
    <Navbar />
    <main className="flex-1"><Outlet /></main>
    <Footer />
  </div>
);

export default function App() {
  return (
    <div dir="rtl">
      <BrowserRouter>
          <SiteProvider>
            <MeProvider>
              <ScrollTop />
              <Routes>
                <Route element={<SiteLayout />}>
                  <Route path="/" element={<Home />} />
                  <Route path="/courses" element={<Courses />} />
                  <Route path="/upcoming" element={<Courses upcoming />} />
                  <Route path="/courses/:ref" element={<CourseDetail />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/faq" element={<FAQ />} />
                  <Route path="/verify" element={<Verify />} />
                  <Route path="/privacy" element={<InfoPage kind="privacy" />} />
                  <Route path="/terms" element={<InfoPage kind="terms" />} />
                  <Route path="/refund" element={<InfoPage kind="refund" />} />
                  <Route path="/sign-in" element={<SignInPage />} />
                  <Route path="/sign-up" element={<SignUpPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/verify-email" element={<VerifyEmailPage />} />
                  <Route path="/enroll/:id" element={<RequireAuth><Enroll /></RequireAuth>} />
                  <Route path="/payment/result" element={<RequireAuth><PaymentResult /></RequireAuth>} />
                  <Route path="/account" element={<RequireAuth><Account /></RequireAuth>} />
                </Route>
                <Route path="/admin" element={<RequireAuth><AdminLayout /></RequireAuth>}>
                  <Route index element={<AdminDashboard />} />
                  <Route path="courses" element={<AdminCourses />} />
                  <Route path="students" element={<AdminStudents />} />
                  <Route path="payments" element={<AdminPayments />} />
                  <Route path="coupons" element={<AdminCoupons />} />
                  <Route path="meetings" element={<AdminMeetings />} />
                  <Route path="reports" element={<AdminReports />} />
                  <Route path="content" element={<AdminContent />} />
                  <Route path="system" element={<AdminSystem />} />
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </MeProvider>
          </SiteProvider>
      </BrowserRouter>
      <Toaster position="top-center" richColors dir="rtl" toastOptions={{ style: { fontFamily: "'IBM Plex Sans Arabic', 'Cairo', sans-serif" } }} />
    </div>
  );
}
